import { useTranslation } from 'react-i18next';
import { federationContacts, federationSocial } from '@/mocks/home';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'kk';

  const isEn = lang.startsWith('en');
  const isRu = lang.startsWith('ru');

  const pageLinks = [
    { label: isEn ? 'Home' : isRu ? 'Главная' : 'Басты бет', href: '#hero' },
    { label: isEn ? 'About us' : isRu ? 'О нас' : 'Біз туралы', href: '#about' },
    { label: isEn ? 'Disciplines' : isRu ? 'Направления' : 'Бағыттар', href: '#disciplines' },
    { label: isEn ? 'Goals' : isRu ? 'Цели' : 'Мақсаттар', href: '#goals' },
    { label: isEn ? 'Events' : isRu ? 'Мероприятия' : 'Іс-шаралар', href: '#events' },
    { label: isEn ? 'Contacts' : isRu ? 'Контакты' : 'Байланыс', href: '#contacts' },
  ];

  return (
    <footer id="contacts" className="relative bg-background-950 border-t border-background-800/50">
      <div className="absolute inset-0 tactical-grid opacity-10 pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pt-16 md:pt-20 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8 mb-12 md:mb-16">
          {/* Logo & description */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 flex items-center justify-center flex-shrink-0">
                <img
                  src="/favicon.png"
                  alt="Federation Logo"
                  className="w-full h-full object-contain filter invert"
                />
              </div>
              <div>
                <span className="font-heading text-foreground-100 text-xs font-semibold tracking-wide uppercase leading-tight block">
                  {isEn ? 'National Federation' : isRu ? 'Национальная Федерация' : '«Әскери-қолданбалы спорт түрлерінің Ұлттық Федерациясы»'}
                </span>
                <span className="text-foreground-700 text-[0.6rem] tracking-widest uppercase">
                  {isEn ? 'of military applied sports' : isRu ? 'военно-прикладных видов спорта' : 'қоғамдық бірлестігі'}
                </span>
              </div>
            </div>
            <p className="text-foreground-600 text-sm leading-relaxed mb-5">
              {isEn
                ? 'Public Association «National Federation of Military Applied Sports» — developing shooting sports, tactical training, and youth patriotic education'
                : isRu
                ? 'Общественное объединение «Национальная Федерация по военно-прикладным видам спорта» — развитие стрелкового спорта, тактической подготовки'
                : '«Әскери-қолданбалы спорт түрлерінің Ұлттық Федерациясы» қоғамдық бірлестігі — ату спортын, тактикалық дайындықты және жастарға патриоттық тәрбие беруді дамыту'}
            </p>
            <div className="flex items-center gap-3">
              <a
                href={federationSocial.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center border border-background-700/50 text-foreground-500 hover:border-accent-500/50 hover:text-accent-500 transition-colors duration-300"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line" />
              </a>
              <a
                href={federationSocial.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center border border-background-700/50 text-foreground-500 hover:border-accent-500/50 hover:text-accent-500 transition-colors duration-300"
                aria-label="YouTube"
              >
                <i className="ri-youtube-line" />
              </a>
            </div>
          </div>

          {/* Pages */}
          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground-100 uppercase tracking-widest mb-5">
              {isEn ? 'Pages' : isRu ? 'Страницы' : 'Беттер'}
            </h4>
            <ul className="space-y-3">
              {pageLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-foreground-500 text-sm hover:text-accent-500 transition-colors duration-300 flex items-center gap-2"
                  >
                    <span className="w-1 h-1 bg-accent-500/40" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contacts */}
          <div className="lg:col-span-2">
            <h4 className="font-heading text-sm font-semibold text-foreground-100 uppercase tracking-widest mb-5">
              {t('footer.contacts', isEn ? 'Contact Info' : isRu ? 'Контакты' : 'Байланыс ақпараты')}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center border border-accent-500/20 flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-accent-500 text-sm" />
                </div>
                <p className="text-foreground-500 text-sm leading-relaxed">
                  {isEn ? federationContacts.addressEn : isRu ? federationContacts.addressRu : federationContacts.addressKk}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center border border-accent-500/20 flex-shrink-0 mt-0.5">
                  <i className="ri-mail-line text-accent-500 text-sm" />
                </div>
                <a
                  href={`mailto:${federationContacts.email}`}
                  className="text-foreground-500 text-sm hover:text-accent-500 transition-colors duration-300"
                >
                  {federationContacts.email}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center border border-accent-500/20 flex-shrink-0 mt-0.5">
                  <i className="ri-phone-line text-accent-500 text-sm" />
                </div>
                <a
                  href={`tel:${federationContacts.phone.replace(/\s/g, '')}`}
                  className="text-foreground-500 text-sm hover:text-accent-500 transition-colors duration-300"
                >
                  {federationContacts.phone}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center border border-accent-500/20 flex-shrink-0 mt-0.5">
                  <i className="ri-time-line text-accent-500 text-sm" />
                </div>
                <p className="text-foreground-500 text-sm leading-relaxed">
                  {isEn ? federationContacts.hoursEn : isRu ? federationContacts.hoursRu : federationContacts.hoursKk}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-background-800/50 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-foreground-700 text-xs tracking-wider">
            {isEn ? 'National Federation of military applied sports' : isRu ? 'Национальная Федерация военно-прикладных видов спорта' : '«Әскери-қолданбалы спорт түрлерінің Ұлттық Федерациясы» қоғамдық бірлестігі'}
          </p>
          <p className="text-foreground-700 text-xs tracking-wider">
            &copy; {new Date().getFullYear()}. {t('footer.rights', isEn ? 'All rights reserved.' : isRu ? 'Все права защищены.' : 'Барлық құқықтар қорғалған.')}
          </p>
        </div>
      </div>
    </footer>
  );
}